/**
 * Smaller copies of the site's own pictures, made by Cloudflare's image
 * transformations (Plan AE 2.2): a news card shows its cover at most about
 * 640 px wide but used to download the 1600 px upload. Only pictures on the
 * site itself (a path): an outside link is shown as it is.
 *
 * Transformations are switched on per zone in the Cloudflare dashboard. Until
 * they are, those addresses fail and `dropVariants` puts the original back, so
 * a picture never shows broken.
 */

const own = (url: string) => url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/cdn-cgi/');

/** The address of a copy `width` px wide (never wider than the original), or null for an outside picture. */
export function resized(url: string, width: number): string | null {
  return own(url) ? `/cdn-cgi/image/width=${width},quality=80,format=auto,fit=scale-down${url}` : null;
}

/**
 * srcset for a picture shown at up to `widths`, and the largest a copy at the
 * original's width, never the original itself: an original is a JPEG straight
 * from the camera (2048 px, ~350 KB), its copy the same size in AVIF or WebP at a
 * third of that. Phones and Retina screens asked for the largest entry and so
 * downloaded every original (3.4 MB on the HBAAC page, 30/09/2026). The original
 * is shown only if the copies fail (`dropVariants`). Undefined for an outside picture.
 */
export function srcSetFor(url: string, widths: readonly number[], originalWidth = 1600): string | undefined {
  if (!own(url)) return undefined;
  // A 1280 step between the page's own widths and the largest: without it a picture
  // needing ~1000 px (a 360 px phone at 3x, a 480 px tile at 2x) took the 1600 copy.
  const steps = originalWidth > 1280 ? [1280] : [];
  const all = [...new Set([...widths, ...steps, originalWidth])].filter((w) => w <= originalWidth).sort((a, b) => a - b);
  return all.map((w) => `${resized(url, w)} ${w}w`).join(', ');
}

/** onError: the copies failed (transformations off), so show the original. */
export function dropVariants(img: HTMLImageElement, original: string): void {
  if (!img.getAttribute('srcset')) return;
  img.removeAttribute('srcset');
  img.removeAttribute('sizes');
  img.src = original;
}

/**
 * srcset of exactly these copies, with no larger step and no copy at the
 * original's width: for a small picture whose largest useful copy is known (a
 * news card is at most ~415 px wide, so 800 px covers it at 2x). A phone at 3x
 * otherwise took the 1280 copy, twice the bytes, for a card 358 px wide. Undefined
 * for an outside picture.
 */
export function srcSetUpTo(url: string, widths: readonly number[]): string | undefined {
  if (!own(url)) return undefined;
  return [...new Set(widths)].sort((a, b) => a - b).map((w) => `${resized(url, w)} ${w}w`).join(', ');
}
